import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { ServiceSelectionEnum } from '../../../../../../core/enums/applications/service-selection-enum';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { ApplicationService } from '../../../../shared/services/application/application.service';
import { ApplicationPageFacadeService } from '../../../shared/services/application-page-facade/application-page-facade.service';
import { SubmitToEwaFormSubmissionComponent } from '../shared/components/submit-to-ewa-form-submission/submit-to-ewa-form-submission.component';
import { SubmitToEwaPageBaseComponent } from '../shared/components/submit-to-ewa-page-base/submit-to-ewa-page-base.component';
import { EwaSubmissionResponse } from '../shared/models/ewa-submission-response';
import { SubmitToEwaDetailsResponse } from '../shared/models/submit-to-ewa-details-response';
import { SubmitToEwaFormService } from '../shared/services/submit-to-ewa-form.service';
import { PublicSafetyCustomerSubmitToEwaFormModel } from './models/public-safety-customer-submit-to-ewa-form-model';
import { PublicSafetyCustomerSubmitToEwaRequest } from './models/public-safety-customer-submit-to-ewa-request';

@Component({
    selector: 'app-public-safety-customer-submit-to-ewa',
    templateUrl: './public-safety-customer-submit-to-ewa.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        SubmitToEwaFormSubmissionComponent,
    ],
})
export class PublicSafetyCustomerSubmitToEwaComponent extends SubmitToEwaPageBaseComponent<
    PublicSafetyCustomerSubmitToEwaRequest,
    PublicSafetyCustomerSubmitToEwaFormModel
> {
    /**
     * The default service selection for this form.
     */
    readonly serviceSelection: ServiceSelectionEnum =
        ServiceSelectionEnum.EXPRESS;

    constructor(
        protected override readonly route: ActivatedRoute,
        protected override readonly router: Router,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        private readonly submitToEwaService: SubmitToEwaFormService,
        private readonly applicationService: ApplicationService,
    ) {
        super(route, router, applicationPageFacadeService);
    }
    override form: FormGroup<
        FormGroupType<PublicSafetyCustomerSubmitToEwaFormModel>
    > = new FormGroup<FormGroupType<PublicSafetyCustomerSubmitToEwaFormModel>>(
        {},
    );

    /**
     * Submits the form to the EWA. Will do a modal popup to confirm the submission.
     * Then, will submit the form and redirect you to the dashboard.
     */
    handoverToEwaSubmission(): void {
        this.submitToEwaService.handoverToEwaSubmission(
            () => this.saveFormDataToApi(this.generateRequestObject(true)),
            () => {
                /**
                 * This is needed to prevent the guard from blocking the navigation
                 * since the submission has already been made and the user is being redirected.
                 */
                this.hasChanges = false;
            },
        );
    }

    /**
     * Determines if the user is allowed to do a final submission.
     * @returns whether the user is allowed to do a final submission
     */
    enableSubmission(): boolean {
        return !this.applicationState?.handoverToEwa;
    }

    protected override bindForm(response: SubmitToEwaDetailsResponse): void {
        this.form.patchValue(response);
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<SubmitToEwaDetailsResponse> {
        return this.applicationService.getDataForFinalSubmission(applicationId);
    }

    protected override saveFormDataToApi(
        request: PublicSafetyCustomerSubmitToEwaRequest,
    ): Observable<EwaSubmissionResponse> {
        return this.applicationService.finalSubmissionToEwa(request);
    }

    protected override generateRequestObject(
        isFinalSubmission: boolean = false,
    ): PublicSafetyCustomerSubmitToEwaRequest {
        const formValue = this.form
            .value as Required<PublicSafetyCustomerSubmitToEwaRequest>;

        return {
            ...formValue,
            submitToEwa: isFinalSubmission,
            applicationId: this.applicationId!,
        };
    }
}
